/**
 * The site's printers as set up in BOB (task 2449; Admin > Printers, each with the area it serves): Zebra label
 * printers and paper printers on the warehouse network.
 *
 * `GET /api/printers` is read each time someone logs in and the last answer is kept on the tablet,
 * so a tablet with the internet down still knows the site's printers. A site setting, not anyone's
 * work: it is not cleared at a session boundary. A paper sheet goes through Android's print dialog,
 * which finds the printer itself; the list says which printers serve which area, and is what a
 * label sent with `print-raw` is addressed to.
 */

export type PrinterKind = 'Label' | 'Paper'

export type SitePrinter = {
  id: number
  name: string
  kind: PrinterKind
  address: string
  port: number
  dpi: number | null
  area: string | null
}

const KEY = 'dockautomation.printers'

/** What the server said, made safe: active printers with a name and an address. Anything else is left out. */
export function parsePrinters(value: unknown): SitePrinter[] {
  if (!Array.isArray(value)) return []
  const list: SitePrinter[] = []
  for (const entry of value) {
    const row = entry && typeof entry === 'object' ? entry as Record<string, unknown> : {}
    const kind = row.kind === 'Label' || row.kind === 'Paper' ? row.kind : null
    const name = typeof row.name === 'string' ? row.name.trim() : ''
    const address = typeof row.address === 'string' ? row.address.trim() : ''
    if (!kind || !name || !address || row.isActive === false || typeof row.id !== 'number') continue
    list.push({
      id: row.id,
      name,
      kind,
      address,
      port: typeof row.port === 'number' ? row.port : kind === 'Label' ? 9100 : 631,
      dpi: typeof row.dpi === 'number' ? row.dpi : null,
      area: typeof row.area === 'string' && row.area.trim() ? row.area.trim() : null,
    })
  }
  return list
}

type Store = Pick<Storage, 'getItem' | 'setItem'>

function storage(): Store | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

export function loadPrinters(store: Store | null = storage()): SitePrinter[] {
  try {
    return parsePrinters(JSON.parse(store?.getItem(KEY) ?? '[]'))
  } catch {
    return []
  }
}

export function savePrinters(list: SitePrinter[], store: Store | null = storage()): void {
  try {
    store?.setItem(KEY, JSON.stringify(list.map(p => ({ ...p, isActive: true }))))
  } catch {
    // Storage unavailable: the tablet reads the list again at the next log-in.
  }
}

/** The paper printers, those serving this area first, then by name. */
export function paperPrinters(list: readonly SitePrinter[], area?: string | null): SitePrinter[] {
  const here = (area ?? '').trim().toUpperCase()
  return list
    .filter(p => p.kind === 'Paper')
    .sort((a, b) => {
      const ah = here !== '' && (a.area ?? '').toUpperCase() === here ? 0 : 1
      const bh = here !== '' && (b.area ?? '').toUpperCase() === here ? 0 : 1
      return ah - bh || a.name.localeCompare(b.name)
    })
}
